import type { Action } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
	consumeSocialRedirectReturn,
	SocialRedirectProvider,
} from '../../services/socialLogins/socialRedirect.web';
import {
	appleLoginRequest,
	facebookLoginRequest,
	googleLoginRequest,
} from '../../store/auth/authActions';
import { navigationRef } from '../helpers/navigationService';
import { toast } from '../helpers/toastUtils';

const LOGIN_ACTION: Record<SocialRedirectProvider, (payload: { idToken: string }) => Action> = {
	google: googleLoginRequest,
	apple: appleLoginRequest,
	facebook: facebookLoginRequest,
};

const PROVIDER_LABEL: Record<SocialRedirectProvider, string> = {
	google: 'Google',
	apple: 'Apple',
	facebook: 'Facebook',
};

/** Runs `callback` once the navigation container is usable; returns the cancel. */
const whenNavigationReady = (callback: () => void): (() => void) => {
	if (navigationRef.isReady()) {
		callback();
		return () => {};
	}
	const started = Date.now();
	const timer = setInterval(() => {
		if (navigationRef.isReady()) {
			clearInterval(timer);
			callback();
		} else if (Date.now() - started > 15000) {
			clearInterval(timer);
			callback();
		}
	}, 50);
	return () => clearInterval(timer);
};

/**
 * Finishes a redirect-based social login (see `socialRedirect.web.ts`) when the web app
 * boots on the provider's return URL: opens the login screen, so the usual session
 * confirmation can show if the backend asks for it, and dispatches the same login action
 * the popup flow dispatches, with the token the provider sent back.
 */
export const useSocialRedirectReturn = () => {
	const dispatch = useDispatch();

	useEffect(() => {
		const result = consumeSocialRedirectReturn();
		if (!result) return;

		return whenNavigationReady(() => {
			if ('error' in result) {
				toast.error(
					'auth.login_failed_title',
					`${PROVIDER_LABEL[result.provider]} login failed`,
					result.error,
				);
				return;
			}

			try {
				navigationRef.navigate('Login');
			} catch (error) {
				console.warn('[socialRedirect] could not open the login screen:', error);
			}
			dispatch(LOGIN_ACTION[result.provider]({ idToken: result.token }));
		});
	}, [dispatch]);
};
